import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getSenderWarnings, verifySavedSender, SENDER_WARNING_EVENT, SENDER_STATUS_EVENT } from '../services/emailDeliveryService';
import './SenderWarning.css';

export function SenderWarningToast() {
  const [warning, setWarning] = useState(null);
  useEffect(() => {
    const receive = (event) => setWarning(event.detail);
    window.addEventListener(SENDER_WARNING_EVENT, receive);
    return () => window.removeEventListener(SENDER_WARNING_EVENT, receive);
  }, []);
  if (!warning) return null;
  return (
    <aside className="sender-warning sender-warning-toast" role="alert">
      <button type="button" className="sender-warning-dismiss" aria-label="Dismiss sender notification" onClick={() => setWarning(null)}>×</button>
      <strong>Custom sender needs attention</strong>
      <p>{warning.message}</p>
      <Link to="/admin/email-manager?sender=fix" onClick={() => setWarning(null)}>Fix custom sender</Link>
    </aside>
  );
}

export function SenderWarningBanner({ onFix }) {
  const [warnings, setWarnings] = useState([]);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const update = () => setRefresh(value => value + 1);
    window.addEventListener(SENDER_STATUS_EVENT, update);
    window.addEventListener('focus', update);
    return () => {
      window.removeEventListener(SENDER_STATUS_EVENT, update);
      window.removeEventListener('focus', update);
    };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    getSenderWarnings(controller.signal).then(items => {
      if (!controller.signal.aborted) { setWarnings(items); setError(''); }
    }).catch(failure => {
      if (!controller.signal.aborted) setError(failure.message);
    });
    return () => controller.abort();
  }, [refresh]);
  if (!warnings.length && !error) return null;
  return (
    <section className="sender-warning" role="status" aria-label="Custom email sender status">
      {warnings.length > 0 && <>
        <strong>Fix your custom email sender</strong>
        <p>Your custom sender needs attention. Update and verify its credentials to send from your email address again.</p>
        <ul>{warnings.map(item => <li key={item.senderEmail}>{item.senderEmail}</li>)}</ul>
        {onFix
          ? <button type="button" onClick={onFix}>Fix custom sender</button>
          : <Link to="/admin/email-manager?sender=fix">Fix custom sender</Link>}
        <VerifySenderButton />
      </>}
      {error && <p>{error} <button type="button" onClick={() => setRefresh(value => value + 1)}>Refresh status</button></p>}
    </section>
  );
}

export function VerifySenderButton() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  const verify = async () => {
    setBusy(true);
    setResult('');
    try { await verifySavedSender(); setResult('Custom sender verified. No email was sent.'); }
    catch (error) { setResult(error.message || 'Could not verify your sender.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="sender-verify">
      <p>Save any changes first, then verify the saved credentials. Verification does not send an email.</p>
      <button type="button" disabled={busy} onClick={verify}>{busy ? 'Verifying…' : 'Verify saved sender'}</button>
      {result && <p role="status">{result}</p>}
    </div>
  );
}
